import type { MaybeRefOrGetter, Ref } from 'vue'
import type { BaseItem } from '~/models/Content/BaseItem'
import { computed, toValue } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import useSearchHelper from '~/composables/useSearchHelper'
import useContentApi from '~/api/contentApi'

const contentCache = new Map<number, BaseItem>()

export interface ActivitiesContentResult {
  data: Ref<BaseItem[] | undefined>
  isLoading: Ref<boolean>
  isFetching: Ref<boolean>
  error: Ref<unknown>
  refetch: () => Promise<unknown>
}

interface ActivityLike { contentId?: number | null }

interface ActivitiesContentOptions {
  page?: MaybeRefOrGetter<number | undefined>
  pageSize?: MaybeRefOrGetter<number | undefined>
  orderedIds?: MaybeRefOrGetter<number[] | undefined>
}

export function useActivitiesContentQuery(
  activitiesInput: MaybeRefOrGetter<ActivityLike[] | undefined>,
  options?: ActivitiesContentOptions,
): ActivitiesContentResult {
  const { findContents } = useContentApi()
  const { cardFields } = useSearchHelper()

  const contentIds = computed<number[]>(() => {
    const activities = toValue(activitiesInput) ?? []
    const ids = activities.map(activity => Number(activity?.contentId))
    return Array.from(new Set(ids.filter((id): id is number => typeof id === 'number' && Number.isFinite(id) && id > 0)))
  })

  const orderedContentIds = computed<number[]>(() => {
    const provided = options?.orderedIds ? toValue(options.orderedIds) : undefined
    if (Array.isArray(provided) && provided.length > 0) {
      const seen = new Set<number>()
      const out: number[] = []
      for (const idRaw of provided) {
        const id = Number(idRaw)
        if (Number.isFinite(id) && id > 0 && !seen.has(id)) {
          seen.add(id)
          out.push(id)
        }
      }
      return out
    }
    return contentIds.value
  })

  const pageSize = computed(() => {
    const size = options?.pageSize ? Number(toValue(options.pageSize)) : undefined
    if (!size || !Number.isFinite(size) || size <= 0) {
      return orderedContentIds.value.length || 0
    }
    return size
  })

  const pageIndex = computed(() => {
    const page = options?.page ? Number(toValue(options.page)) : 1
    const p = Number.isFinite(page) && page > 0 ? page - 1 : 0
    return p
  })

  const pagedContentIds = computed(() => {
    if (!pageSize.value || pageSize.value >= orderedContentIds.value.length) {
      return orderedContentIds.value
    }
    const start = pageIndex.value * pageSize.value
    const end = start + pageSize.value
    return orderedContentIds.value.slice(start, end)
  })

  const query = useQuery({
    queryKey: computed(() => ['activities-contents', pagedContentIds.value.join(','), pageSize.value, pageIndex.value]),
    enabled: computed(() => pagedContentIds.value.length > 0),
    queryFn: async () => {
      const ids = pagedContentIds.value
      const missingIds = ids.filter(id => !contentCache.has(id))

      if (missingIds.length > 0) {
        const fetched = await findContents<BaseItem>({
          contentIdCriterion: missingIds,
          mainLocationCriterion: true,
        }, missingIds.length, 0, cardFields)

        for (const item of fetched) {
          contentCache.set(item.contentId, item)
        }
      }

      return ids
        .map(id => contentCache.get(id))
        .filter((v): v is BaseItem => !!v)
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  }
}
